import React, { useMemo } from 'react';
import { Gift, Calendar, User } from 'lucide-react';
import { PageLayout, PageHeader, PageCard, pageStyles, useIsDark } from '../PageLayout';

const normalizeNumber = (v: any) => v ? String(v).replace(/[^0-9]/g, '').replace(/^0+/, '') : '';
const normalizeName = (n: any) => n ? n.replace(/[:.;,]/g, ' ').replace(/\s+/g, ' ').trim() : '';

const checkMatch = (allowed: any[], c: any) => {
    if (!allowed || !Array.isArray(allowed)) return false;
    if (allowed.some(u => u.role === 'admin')) return true;
    return allowed.some(u => {
        if (!u) return false;
        const uAdv = normalizeNumber(u.adv), cAdp = normalizeNumber(c.adp), cBtnr = normalizeNumber(c.btnr), uBtnr = normalizeNumber(u.btnr);
        if (uAdv.length > 2 && ((cAdp && uAdv.includes(cAdp)) || (cBtnr && uAdv.includes(cBtnr)))) return true;
        if (uBtnr.length > 2 && ((cAdp && uBtnr.includes(cAdp)) || (cBtnr && uBtnr.includes(cBtnr)))) return true;
        const uName = normalizeName(u.name).toLowerCase(), cAdpName = normalizeName(c.adp).toLowerCase();
        return uName.length > 2 && cAdpName.includes(uName);
    });
};

const isBirthdayUpcoming = (d: any) => {
    if (!d) return false;
    const parseAnyDate = (s: any) => {
        if (!s) return null;
        if (typeof s === 'string' && s.includes('-')) {
            const [y, m, d] = s.split('-');
            return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
        }
        if (typeof s === 'string' && s.includes('.')) {
            const [d, m, y] = s.split('.');
            return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
        }
        return null;
    };
    const dob = parseAnyDate(d);
    if (!dob) return false;
    const today = new Date();
    const next = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
    if (next < today) next.setFullYear(today.getFullYear() + 1);
    const diff = Math.ceil((next.getTime() - today.getTime()) / 86400000);
    return diff >= 0 && diff <= 30;
};

const getNextBirthdayDate = (d: any) => {
    if (!d) return new Date(9999, 11, 31);
    const parseAnyDate = (s: any) => {
        if (!s) return null;
        if (typeof s === 'string' && s.includes('-')) {
            const [y, m, d] = s.split('-');
            return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
        }
        if (typeof s === 'string' && s.includes('.')) {
            const [d, m, y] = s.split('.');
            return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
        }
        return null;
    };
    const dob = parseAnyDate(d);
    if (!dob) return new Date(9999, 11, 31);
    const today = new Date();
    const next = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
    if (next < today) next.setFullYear(today.getFullYear() + 1);
    return next;
};

type BirthdaysViewProps = {
    customers: any[];
    currentUser: any;
    allUsers: any[];
    onSelectCustomer: (c: any) => void;
    onNavigate: (view: string) => void;
};

export default function BirthdaysView({ customers, currentUser, allUsers, onSelectCustomer, onNavigate }: BirthdaysViewProps) {
    const isDark = useIsDark();

    const myCustomers = useMemo(() => {
        if (!currentUser) return [];
        if (currentUser.role === 'admin') return customers;
        const allowedUserIds = [currentUser.id, ...(currentUser.allowedToView || [])];
        const allowed = allUsers.filter((u: any) => allowedUserIds.includes(u.id));
        return customers.filter((c: any) => checkMatch(allowed, c));
    }, [customers, currentUser, allUsers]);

    const upcoming = useMemo(
        () =>
            myCustomers
                .filter((c: any) => isBirthdayUpcoming(c.gebDatum))
                .sort(
                    (a: any, b: any) =>
                        getNextBirthdayDate(a.gebDatum).getTime() -
                        getNextBirthdayDate(b.gebDatum).getTime()
                ),
        [myCustomers]
    );

    const handleClick = (c: any) => {
        onSelectCustomer(c);
        onNavigate('customers');
    };

    return (
        <PageLayout>
            <PageHeader
                title="Geburtstage"
                subtitle="Kunden mit Geburtstag in den nächsten 30 Tagen"
            />
            <PageCard>
                {upcoming.length === 0 ? (
                    <div className={`${pageStyles.empty(isDark)} flex flex-col items-center`}>
                        <Gift size={48} className={`mb-4 ${isDark ? 'text-amber-400/80' : 'text-amber-500'}`} />
                        <p className={isDark ? 'text-slate-300' : ''}>In den nächsten 30 Tagen hat kein sichtbarer Kunde Geburtstag.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className={isDark ? 'bg-slate-800 text-slate-200' : 'bg-slate-50 text-slate-700'}>
                                    <th className="px-3 py-2 text-left"><User size={14} className="inline mr-1" /> Kunde</th>
                                    <th className="px-3 py-2 text-left"><Calendar size={14} className="inline mr-1" /> Geburtstag</th>
                                    <th className="px-3 py-2 text-left">In Tagen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {upcoming.map((c: any) => {
                                    const next = getNextBirthdayDate(c.gebDatum);
                                    const days = Math.ceil(
                                        (next.getTime() - new Date().getTime()) / 86400000
                                    );
                                    return (
                                        <tr
                                            key={c.id}
                                            onClick={() => handleClick(c)}
                                            className={`${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-100'} cursor-pointer`}
                                        >
                                            <td className="px-3 py-2">
                                                <div className={isDark ? 'text-white font-medium' : 'text-slate-900 font-medium'}>
                                                    {c.vorname} {c.name}
                                                </div>
                                            </td>
                                            <td className={`px-3 py-2 ${isDark ? 'text-slate-200' : ''}`}>{c.gebDatum || '-'}</td>
                                            <td className={`px-3 py-2 ${isDark ? 'text-slate-200' : ''}`}>{days === 0 ? 'Heute' : days}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </PageCard>
        </PageLayout>
    );
}
